import {
  selectEligibleInvestmentQuotas,
  getInvestmentQuotaAutofill,
} from "@/application/mappers/investments/investmentFormMapper";
import type { FiscalQuota } from "@/domain/beneficiary";
import type { MutableRefObject } from "react";
import { useEffect, useMemo } from "react";
import type { InvestmentFormData } from "@/domain/validation/investmentSchemas";

interface UseInvestmentFormQuotaSyncParams {
  isEditMode: boolean;
  existingInvestmentQuotaUuid: string | undefined;
  fiscalQuotas: FiscalQuota[] | undefined;
  formData: InvestmentFormData;
  updateFormData: (updates: Partial<InvestmentFormData>) => void;
  selectedQuota: FiscalQuota | null;
  setSelectedQuota: (quota: FiscalQuota | null) => void;
  lastAutoFilledQuotaUuid: MutableRefObject<string | null>;
}

export function useInvestmentFormQuotaSync({
  isEditMode,
  existingInvestmentQuotaUuid,
  fiscalQuotas,
  formData,
  updateFormData,
  selectedQuota,
  setSelectedQuota,
  lastAutoFilledQuotaUuid,
}: UseInvestmentFormQuotaSyncParams) {
  const computedQuotas = useMemo(
    () =>
      selectEligibleInvestmentQuotas(
        fiscalQuotas,
        isEditMode ? existingInvestmentQuotaUuid : undefined,
      ),
    [fiscalQuotas, isEditMode, existingInvestmentQuotaUuid],
  );

  useEffect(() => {
    if (formData.quotaId && computedQuotas.length > 0) {
      const q = computedQuotas.find((q) => q.uuid === formData.quotaId);
      setSelectedQuota(q || null);
    } else if (!formData.quotaId) {
      setSelectedQuota(null);
    }
  }, [formData.quotaId, computedQuotas, setSelectedQuota]);

  useEffect(() => {
    const autofill = getInvestmentQuotaAutofill(
      selectedQuota?.uuid
        ? {
            uuid: selectedQuota.uuid,
            default_profitability_percentage: selectedQuota.default_profitability_percentage,
          }
        : null,
      lastAutoFilledQuotaUuid.current,
    );
    lastAutoFilledQuotaUuid.current = autofill.lastQuotaUuid;
    if (autofill.updates) updateFormData(autofill.updates);
  }, [
    selectedQuota?.uuid,
    selectedQuota?.default_profitability_percentage,
    updateFormData,
    lastAutoFilledQuotaUuid,
  ]);

  return computedQuotas;
}
